import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useSalon } from '../store/slice/salonSlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionLink, StatusToggle } from '../components/ActionControls';

function isActiveSalon(salon) {
  return salon?.is_active === 1 || salon?.is_active === true;
}

function formatDate(value) {
  if (!value) return '—';

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

function getSalonInitials(name) {
  const words = (name || 'Salon')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || 'SL';
}

function SalonsPage() {
  const {
    salons,
    loading,
    error,
    fetchSalons,
    deleteSalon,
    toggleSalonActive,
  } = useSalon();
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    is_active: '',
    include_inactive: '1',
    sortBy: '',
  });

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const apiFilters = useMemo(() => {
    const nextFilters = {};
    if (filters.city && filters.city !== 'all') nextFilters.city = filters.city;
    if (filters.is_active === '1' || filters.is_active === '0') {
      nextFilters.is_active = filters.is_active;
    } else if (filters.include_inactive === '1') {
      nextFilters.include_inactive = '1';
    }
    return nextFilters;
  }, [filters.city, filters.is_active, filters.include_inactive]);

  useEffect(() => {
    fetchSalons(apiFilters);
  }, [fetchSalons, apiFilters]);

  const handleFilterChange = (key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const statusFilterValue =
    filters.include_inactive === '1' ? 'all' : filters.is_active || 'active_only';

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'city',
      type: 'select',
      label: 'City',
      value: filters.city || 'all',
      defaultValue: 'all',
      options: [...new Set(salons.map((s) => s.city).filter(Boolean))].sort().map((c) => ({ value: c, label: c })),
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      value: statusFilterValue,
      defaultValue: 'active_only',
      options: [
        { value: 'active_only', label: 'Active only' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
        { value: 'all', label: 'All (incl. inactive)' },
      ],
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'name-asc', label: 'Name (A-Z)' },
        { value: 'name-desc', label: 'Name (Z-A)' },
        { value: 'city-asc', label: 'City (A-Z)' },
      ],
    },
  ];

  const handleFilterChangeWrapper = (key, value) => {
    if (key === 'status') {
      if (value === 'active_only') setFilters((p) => ({ ...p, is_active: '', include_inactive: '0' }));
      else if (value === 'all') setFilters((p) => ({ ...p, is_active: '', include_inactive: '1' }));
      else setFilters((p) => ({ ...p, is_active: value, include_inactive: '0' }));
    } else {
      handleFilterChange(key, value);
    }
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.city) count++;
    if (filters.is_active || filters.include_inactive === '1') count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredSalons = useMemo(() => {
    let result = [...salons];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || '').toLowerCase().includes(term) ||
          (s.city || '').toLowerCase().includes(term) ||
          (s.address_line_1 || '').toLowerCase().includes(term)
      );
    }

    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va = (a[field] || '').toString().toLowerCase();
        let vb = (b[field] || '').toString().toLowerCase();
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [salons, filters.search, filters.sortBy]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      const result = await deleteSalon(id);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Salon deleted successfully');
      }
    }
  };

  const handleToggleActive = async (id, isActive) => {
    const result = await toggleSalonActive(id);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(`Salon marked as ${isActive ? 'inactive' : 'active'}`);
    }
  };

  if (loading && salons.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading salons...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Salons</h1>
          <p className="mt-1 text-slate-500">View and manage all salon listings</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Salons</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{salons.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredSalons.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Active</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            {salons.filter((s) => isActiveSalon(s)).length}
          </p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search by name, city or address..."
        filters={filterConfig}
        onFilterChange={handleFilterChangeWrapper}
        activeCount={activeFilterCount}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filteredSalons.map((salon) => {
          const salonName = salon.name || `Salon #${salon.id}`;
          const isActive = isActiveSalon(salon);
          const primaryContact = salon.phone || salon.email || 'Not added';

          return (
            <div
              key={salon.id}
              className="card-premium group flex h-full flex-col overflow-hidden border-slate-200/80 transition-all duration-250 hover:-translate-y-1 hover:border-amber-200/70 hover:shadow-premium-lg"
            >
              <div className="relative h-28 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-amber-700">
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-white/10" />
                <div className="absolute left-5 right-5 top-4 flex items-start justify-between gap-3">
                  <span className="inline-flex max-w-[70%] rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/90 backdrop-blur">
                    {salon.city || 'Location pending'}
                  </span>
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                      isActive
                        ? 'border-emerald-200/30 bg-emerald-400/15 text-emerald-50'
                        : 'border-white/15 bg-white/10 text-white/80'
                    }`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="relative flex flex-1 flex-col px-5 pb-5">
                <div className="-mt-7 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/70 bg-white shadow-lg ring-4 ring-white">
                  <span className="text-sm font-bold tracking-[0.22em] text-amber-700">
                    {getSalonInitials(salonName)}
                  </span>
                </div>

                <div className="mt-4">
                  <h3 className="min-h-[3rem] text-lg font-semibold leading-6 text-slate-800">
                    {salonName}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">Salon ID #{salon.id}</p>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                    <div className="flex items-start gap-3">
                      <svg
                        className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M12 21c4-4.35 6-7.46 6-10a6 6 0 10-12 0c0 2.54 2 5.65 6 10z"
                        />
                        <circle cx="12" cy="11" r="2.2" strokeWidth={1.8} />
                      </svg>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Address
                        </p>
                        <p
                          className="mt-1 text-sm leading-5 text-slate-600"
                          title={salon.address_line_1 || 'Address not added yet'}
                        >
                          {salon.address_line_1 || 'Address not added yet'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-slate-100 bg-white p-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M3 5h18v14H3V5zm2 2l7 5 7-5"
                          />
                        </svg>
                        Contact
                      </div>
                      <p className="mt-2 break-words text-sm font-medium text-slate-700">
                        {primaryContact}
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-100 bg-white p-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Added
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-700">{formatDate(salon.created_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Visibility
                      </p>
                      <StatusToggle
                        checked={isActive}
                        onChange={() => handleToggleActive(salon.id, isActive)}
                        disabled={loading}
                        label={`Toggle salon status for ${salonName}`}
                        showStateLabel={false}
                        className="mt-2"
                      />
                    </div>
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                        isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {isActive ? 'Live' : 'Hidden'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <ActionLink
                      to={`/salons/${salon.id}`}
                      variant="neutral"
                      className="w-full gap-2"
                    >
                      <span>View</span>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </ActionLink>
                    <ActionButton
                      type="button"
                      variant="danger"
                      onClick={() => handleDelete(salon.id, salonName)}
                      disabled={loading}
                      className="w-full"
                    >
                      Delete
                    </ActionButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filteredSalons.length === 0 && (
        <div className="card-premium py-16 text-center text-slate-500">
          No salons found matching the current filters.
        </div>
      )}
    </div>
  );
}

export default SalonsPage;
